import '../../style/app.scss';
import '../../style/Site.less';

import menuItem = require("../model/menu.item");
import menu = require("../core/menu.dataservice");
import coreSettings = require("../core/coresettings");
import appSettings = require("../core/appsettings");
import { Principal } from '../shared/auth/principal.service';
import { LoginService } from '../shared/auth/login.service';
import { CrewEventManager } from '../shared/event-manager.service';
import { UserCredentials } from '../model/user-credentials';
import { TransitionService } from "@uirouter/angular";

import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

export function initialize(module: ng.IModule) {
    module.controller("ShellController", ShellController);
}

interface IShellViewModel {
    menuItems: menuItem.MenuItem[];
    appIsModal: boolean;
    appSettings: coreSettings.ApplicationSettings;
    applicationUser: any;
    isFrontPage: boolean;
    isProduction: boolean;

    toggleSidnav(): void;
    toggleChildMenu(menuItem: menuItem.MenuItem): void;
    onRootMenuClicked(menuItem: menuItem.MenuItem): void;
}

export class ShellController implements IShellViewModel {

    menuItems: menuItem.MenuItem[] = [];
    appIsModal: boolean;
    appSettings: coreSettings.ApplicationSettings;
    applicationUser: any;
    isProduction: boolean;
    isAuthenticated: boolean;
    isInitialLoad = true;
    isLoading: boolean;
    allowAnonymous: boolean;

    isWindowsAuthentication: boolean;
    loginError: string;

    appInsightsSubject = new Subject();

    userCredentials: UserCredentials = {
        Email: "",
        Password: "",
        TenantName: ""
    };

    static $inject = [
        "$log",
        "$state",
        "$mdSidenav",
        menu.MenuDataService.Id,
        appSettings.AppSettingsService.Id,
        "$transitions",
        "principal",
        "loginService",
        "crewEventManager",
        "$mdMedia"
    ];
    constructor(
        $log: ng.ILogService,
        private $state: ng.ui.IStateService,
        private $mdSidenav: ng.material.ISidenavService,
        private menuDataService: menu.MenuDataService,
        private settings: appSettings.AppSettingsService,
        $transitions: TransitionService,
        principal: Principal,
        private loginService: LoginService,
        eventManager: CrewEventManager,
        public $mdMedia: ng.material.IMedia
    ) {
        $log.info("Shell is up and running...");

        this.appSettings = coreSettings.applicationSettings;

        // Wire up Application Insights initialization
        this.appInsightsSubject
            .pipe(debounceTime(800))
            .subscribe(() => {
                if (!this.isAppInsightsLoaded) {
                    this.settings.getAppSetting2("Azure.ApplicationInsightsKey").subscribe(
                        res => this.initAppInsights(res.body),
                        () => { });
                }
            });

        this.allowAnonymous = this.getAllowAnonymous(this.$state.current.data);

        $transitions.onSuccess(
            {},
            trans => {
                this.allowAnonymous = this.getAllowAnonymous(trans.to().data);
                this.refreshCurrentState();
            });

        this.isProduction = true;

        eventManager.onAuthenticationSuccess(userIdentity => this.onAuthentication(userIdentity, true));
        eventManager.onLogoutSuccess(() => this.onLogout());

        this.settings.isWindowsAuthentication().subscribe(
            res => this.isWindowsAuthentication = res.body,
            () => { });

        if (this.allowAnonymous) {
            this.isInitialLoad = false;
        }
        else {
            this.isInitialLoad = true;
            principal
                .identity()
                .subscribe(
                    identity => {
                        if (identity) {
                            this.onAuthentication(identity);
                        } else {
                            this.onLogout();
                        }

                        this.isInitialLoad = false;
                    },
                    () => {
                        this.onLogout();
                        this.isInitialLoad = false;
                    });
        }
    }

    private onAuthentication(userIdentity: Nema.Crew.Model.ApplicationUser, shouldCreateMenu: boolean = false) {
        this.applicationUser = userIdentity;
        this.isAuthenticated = true;

        if (shouldCreateMenu) {
            this.createMenu(this.menuDataService);
        }

        this.appInsightsSubject.next();

        this.settings.getAppSetting("IsProduction").subscribe(
            res => this.isProduction = res.body,
            () => { });
    };

    private onLogout() {
        this.applicationUser = undefined;
        this.isAuthenticated = false;
        this.$state.go("app.home");
    };

    private isAppInsightsLoaded = false;

    private initAppInsights(instrumentationKey: string) {
        if (instrumentationKey && !this.isAppInsightsLoaded) {
            var appInsights = new ApplicationInsights({
                config: {
                    instrumentationKey: instrumentationKey,
                    enableAutoRouteTracking: true // option to log all route changes
                }
            });
            appInsights.loadAppInsights();

            this.isAppInsightsLoaded = true;
        }
    }

    private createMenu(menuDataService: menu.MenuDataService) {
        menuDataService.getMenuItems().subscribe(
            menuItems => {
                this.menuItems = menuItems;
                this.refreshCurrentState();
            },
            () => { });
    }

    public login(): void {
        if (this.isLoading) {
            return;
        }

        this.isLoading = true;
        this.loginError = "";
        this.loginService
            .login(this.userCredentials)
            .subscribe(
                () => {
                    this.userCredentials.Email = "";
                    this.userCredentials.Password = "";
                    this.userCredentials.TenantName = "";
                    this.isLoading = false;
                },
                () => {
                    this.loginError = "Could not log in user";
                    this.isLoading = false;
                });
    }

    public keyPressed(keyEvent) {
        //When <enter> key is pressed, login
        if (keyEvent.which === 13)
            this.login();
    }

    public openMenu(mdOpenMenu, ev): void {
        mdOpenMenu(ev);
    }

    public logout(): void {
        this.loginService.logout();
    }

    public changePassword(): void {
        this.$state.go("app.password.change");
    }

    public get isAuthenticatedOrAnonymous(): boolean {
        return this.isAuthenticated || this.allowAnonymous;
    }

    private refreshCurrentState(): void {
        this.openSelectedMenu();
    }

    public get isFrontPage(): boolean {
        return this.$state.current.name === "app.home";
    }

    public toggleSidnav() {
        this.sidebar.toggle();
    }

    private _sidebarHasLoaded = false;

    private get sidebarHasLoaded(): boolean {
        if (this._sidebarHasLoaded) {
            return true;
        }

        this.$mdSidenav("left", true)
            .then(() => this._sidebarHasLoaded = true)
            .catch(() => this._sidebarHasLoaded = false);

        return false;
    }

    private get sidebar(): ng.material.ISidenavObject {
        return this.$mdSidenav("left");
    }

    public sidebarIsPinned: boolean;

    public toggleSidebarPinned(): void {
        this.sidebarIsPinned = !this.sidebarIsPinned;
        if (!this.sidebarIsPinned && this.sidebarHasLoaded) {
            this.sidebar.close();
        }
    }

    public get disableSidebarMenuButton(): boolean {
        return !this.sidebarHasLoaded || this.sidebar.isLockedOpen();
    }

    public get sidebarPinIcon(): string {
        return !this.sidebarHasLoaded ? 'hourglass_empty' : (this.sidebarIsPinned ? 'chevron_left' : 'chevron_right');
    }

    public get sidebarPinButtonTooltip(): string {
        return !this.sidebarHasLoaded ? 'Loading' : (this.sidebarIsPinned ? 'Unpin menu' : 'Pin menu');
    }

    public get sidebarMenuButtonTooltip(): string {
        return !this.sidebarHasLoaded ? 'Loading' : ((this.sidebar.isOpen() ? "Hide" : "Show") + " menu");
    }

    public toggleChildMenu(menuItem: menuItem.MenuItem) {
        if (!menuItem.isChildMenuOpen) {
            menuItem.toggleChildMenu(true);
        } else {
            menuItem.toggleChildMenu(false);
        }

        this.toggleChildMenus(false, this.menuItems.filter(m => m != menuItem));
    }

    public toggleChildMenus(isVisible: boolean, menuItems: menuItem.MenuItem[]) {
        for (var i = 0; i < menuItems.length; i++) {
            menuItems[i].toggleChildMenu(isVisible);
        }
    }

    public onRootMenuClicked(menuItem: menuItem.MenuItem) {
        if (!menuItem.hasChildren && menuItem.uiSref) {
            this.$state.go(menuItem.uiSref);
        }

        this.toggleChildMenu(menuItem);
    }

    private openSelectedMenu() {
        // start by collapsing the entire menu
        for (let menuItem of this.menuItems) {
            menuItem.toggleChildMenu(false);
        }

        for (let menuItem of this.menuItems) {
            for (let mi of menuItem.childMenuItems) {
                if (mi.uiSref == this.$state.current.name || mi.uiSrefAlias == this.$state.current.name) {
                    menuItem.toggleChildMenu(true);
                    break;
                }
            }
        }
    }

    private getAllowAnonymous(stateData: any): boolean {
        return !!stateData && !!stateData.allowAnonymous;
    }
}

var _appModal = false;
var _mainVm;
var _mainScope: ng.IScope;

export function enterAppModal() {
    _appModal = true;
    if (_mainVm) {
        _mainVm.appIsModal = _appModal;
    }
    if (_mainScope && !_mainScope.$$phase) {

        _mainScope.$apply();
    }
}

export function leaveAppModal() {
    _appModal = false;
    if (_mainVm) {
        _mainVm.appIsModal = _appModal;
    }
    if (_mainScope && !_mainScope.$$phase) {
        _mainScope.$apply();
    }
}


// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/@angularclass/hmr-loader!./node_modules/tslint-loader!./client/app/layout/shell.controller.ts